<script lang="ts" setup>
import { cloneDeep, isEqual } from "es-toolkit";
import type { KitResponse } from "mws-catalog-ts-sdk";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

import BaseButton from "@/common/components/BaseButton.vue";
import CardVue from "@/common/components/card/CardVue.vue";
import ConfirmationModal from "@/common/components/ConfirmationModal.vue";
import FormSection from "@/common/components/FormSection/FormSection.vue";
import HorizontalTabs from "@/common/components/HorizontalTabs/HorizontalTabs.vue";
import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import LoadingSpinner from "@/common/components/LoadingSpinner.vue";
import ModalVue from "@/common/components/ModalVue.vue";
import type { FormSectionDetails } from "@/common/types";
import { downloadExternalFile } from "@/common/utils/downloadExternalFile";
import {
  downloadZipFile,
  type ZipFileItem,
} from "@/common/utils/downloadZipFile";
import { getErrorMessage } from "@/common/utils/getErrorMessage";
import { imgixThumbnail } from "@/common/utils/imgixThumbnail";
import {
  useDownloadCatalogKitMockupMutation,
  useUpdateCatalogKitMockupMutation,
} from "@/kits/api/queries";
import CatalogKitBulkUploader from "@/kits/components/CatalogKitBulkUploader.vue";
import FrameAssetModal from "@/teams/components/FrameAssetModal.vue";
import { useCurrentUser } from "@/users/public/composables";

definePage({
  alias: ["/teams/:teamId?/kits/:kitId/mockups"],
  meta: {
    auth: ["Kits.Read"],
  },
});

const { kit } = defineProps<{
  kit: KitResponse;
}>();

const isImagePreviewModalOpen = ref(false);
const isPhotoUploadModalOpen = ref(false);
const clearUploaderInput = ref(false);
const showDeleteAllMockupsConfirmation = ref(false);
const { userHasPermission } = useCurrentUser();

const previewImageUrl = ref<string>();
const toast = useToast();
const router = useRouter();

const selectedMockup = ref<KitFormValue | null>(null);

const isFrameAssetModalOpen = ref(false);

const openFrameAssetModal = (mockup: KitFormValue) => {
  selectedMockup.value = cloneDeep(mockup);
  isFrameAssetModalOpen.value = true;
};

const closeFrameAssetModal = () => {
  isFrameAssetModalOpen.value = false;
  selectedMockup.value = null;
};

type KitFormValue = {
  id: string;
  kitId: string;
  athleteId?: string | null;
  shirtName?: string | null;
  shirtNumber?: number | null;
  isArchived: boolean;
  isApproved: boolean;
  imageUrls: {
    imageUrl: string;
  }[];
  images: string[];
  mockupSetId?: string | null;
};
const kitMockupFormValue = ref<KitFormValue[]>([]);

const currentImageIndexes = ref<Record<string, number>>({});

const imageLoading = ref<Record<string, boolean>>({});

const kitMockups = computed<KitFormValue[]>(() => {
  return (
    kit?.mockups.map((mockup) => ({
      id: mockup.id,
      kitId: kit.id,
      athleteId: mockup.athleteId,
      shirtName: mockup.shirtName,
      shirtNumber: mockup.shirtNumber,
      isArchived: mockup.isArchived,
      isApproved: mockup.isApproved,
      imageUrls: mockup.images.map((image) => ({ imageUrl: image })),
      images: mockup.images,
      mockupSetId: mockup.mockupSetId,
    })) ?? []
  );
});

// Initialize current image indexes
watch(
  kitMockups,
  (newVal) => {
    newVal.forEach((mockup) => {
      if (!(mockup.id in currentImageIndexes.value)) {
        currentImageIndexes.value[mockup.id] = 0;
      }
    });
  },
  { immediate: true },
);

watch(
  kitMockups,
  (newVal, oldVal) => {
    if (isEqual(oldVal, newVal)) {
      return;
    }
    kitMockupFormValue.value = cloneDeep(newVal);
  },
  { immediate: true },
);

const { mutateAsync: downloadMockups } = useDownloadCatalogKitMockupMutation();
const { mutateAsync: updateKitMockup } = useUpdateCatalogKitMockupMutation();

const getCurrentImage = (mockup: KitFormValue) => {
  const currentIndex = currentImageIndexes.value[mockup.id] || 0;

  return mockup.imageUrls[currentIndex] || mockup.imageUrls[0] || null;
};

const getCurrentImageIndex = (mockupId: string) => {
  return currentImageIndexes.value[mockupId] || 0;
};

const nextImage = (mockupId: string) => {
  const mockup = kitMockups.value.find((m) => m.id === mockupId);
  if (!mockup) return;

  imageLoading.value[mockupId] = true;

  const currentIndex = currentImageIndexes.value[mockupId] || 0;
  const nextIndex = (currentIndex + 1) % mockup.imageUrls.length;
  currentImageIndexes.value[mockupId] = nextIndex;
};

const previousImage = (mockupId: string) => {
  const mockup = kitMockups.value.find((m) => m.id === mockupId);
  if (!mockup) return;

  imageLoading.value[mockupId] = true;

  const currentIndex = currentImageIndexes.value[mockupId] || 0;
  const prevIndex =
    currentIndex === 0 ? mockup.imageUrls.length - 1 : currentIndex - 1;
  currentImageIndexes.value[mockupId] = prevIndex;
};

const triggerUpload = (mockupId: string) => {
  const uploaderElement = document.getElementById(`uploader-${mockupId}`);
  if (uploaderElement && uploaderElement instanceof HTMLInputElement) {
    uploaderElement.click();
  }
};

const handleImageUpload = async (
  mockup: KitFormValue,
  imageUrls: string[] | string,
) => {
  if (!imageUrls || (Array.isArray(imageUrls) && imageUrls.length === 0))
    return;

  if (typeof imageUrls === "string") {
    imageUrls = [imageUrls];
  }

  clearUploaderInput.value = true;
  await updateKitMockup({
    kitId: kit.id,
    kitMockupId: mockup.id,
    updateKitMockupRequest: {
      id: mockup.id,
      shirtName: mockup.shirtName,
      shirtNumber: mockup.shirtNumber,
      images: [
        ...mockup.imageUrls.map((img) => img.imageUrl),
        ...imageUrls,
      ].filter((img) => !!img),
    },
  });
  clearUploaderInput.value = false;
  toast.success("Mockup updated successfully");
};

const onDownloadAllMockups = async () => {
  const kitName = kit.name || "Kit-mockup";

  try {
    toast.info("Downloading mockups...", { timeout: 0 });
    await downloadMockups({ kitId: kit.id, kitName });
    toast.clear();
    toast.success("Mockups downloaded successfully");
  } catch (e) {
    toast.clear();
    toast.error(getErrorMessage(e)[0]);
  }
};

const onDownloadMockup = async (mockup: KitFormValue) => {
  if (mockup.imageUrls.length === 0) {
    toast.error("No images to download");

    return;
  }

  if (mockup.imageUrls.length === 1) {
    // Download single image directly
    const imageUrl = mockup.imageUrls[0]?.imageUrl;
    const fileName =
      imageUrl?.split("/").pop() ??
      `${mockup.shirtNumber} + ${mockup.shirtName}`;
    downloadExternalFile({
      uri: imageUrl,
      filename: fileName,
    });
    toast.success("Image downloaded successfully");
  } else {
    // Create zip file for multiple images
    toast.info("Preparing zip file...", { timeout: 0 });
    const zipItems: ZipFileItem[] = mockup.imageUrls.map((image) => {
      const fileName = image.imageUrl.split("/").pop() || "image";

      return {
        url: image.imageUrl,
        filename: fileName,
      };
    });

    await downloadZipFile({
      items: zipItems,
      zipFilename: `${mockup.shirtNumber} + ${mockup.shirtName}.zip`,
    });

    toast.clear();
  }
};

const onRemoveImage = async (mockupId: string, urlToRemove: string) => {
  const currentMockup = kitMockupFormValue.value.find((m) => m.id === mockupId);
  if (!currentMockup) return;

  const newImages = currentMockup.imageUrls.filter(
    (img) => img.imageUrl !== urlToRemove,
  );

  await updateKitMockup({
    kitId: kit.id,
    kitMockupId: mockupId,
    updateKitMockupRequest: {
      id: mockupId,
      shirtName: currentMockup.shirtName,
      shirtNumber: currentMockup.shirtNumber,
      images: newImages.map((img) => img.imageUrl).filter((img) => !!img),
    },
  });
  toast.success("Image removed successfully");
};

const onDeleteAllMockups = async () => {
  showDeleteAllMockupsConfirmation.value = true;
};

const confirmDeleteAllMockups = async () => {
  showDeleteAllMockupsConfirmation.value = false;
  toast.info("Deleting all mockups...", { timeout: 0 });
  for (const mockup of kitMockupFormValue.value) {
    await updateKitMockup({
      kitId: kit.id,
      kitMockupId: mockup.id,
      updateKitMockupRequest: {
        id: mockup.id,
        shirtName: mockup.shirtName,
        shirtNumber: mockup.shirtNumber,
        images: [],
      },
    });
  }
  toast.clear();
  toast.success("All mockups deleted successfully");
};

const handleImagePreview = (imageUrl: string) => {
  previewImageUrl.value = imageUrl;
  isImagePreviewModalOpen.value = true;
};

const detailsList = computed<FormSectionDetails>(() => {
  const baseDetails = {
    dateModified: { type: "date", value: kit?.dateModified },
    dateCreated: { type: "date", value: kit?.dateCreated },
  } as FormSectionDetails;

  if (userHasPermission("Admin.Access")) {
    return {
      modifiedBy: {
        type: "user",
        value: kit?.modifiedBy,
      },
      dateModified: { type: "date", value: kit?.dateModified },
      createdBy: { type: "user", value: kit?.createdBy },
      dateCreated: { type: "date", value: kit?.dateCreated },
    } as FormSectionDetails;
  }

  return baseDetails;
});

const activeTab = ref<"active" | "missing" | "pending" | "archived">("active");

const mockupsByTab = computed(() => {
  const allMockups = kitMockups.value;
  const activeMockups = allMockups.filter(
    (mockup) => !mockup.isArchived && mockup.isApproved,
  );
  const missingMockups = allMockups.filter(
    (mockup) =>
      mockup.imageUrls.length === 0 && mockup.isApproved && !mockup.isArchived,
  );
  const pendingMockups = allMockups.filter(
    (mockup) => !mockup.isApproved && !mockup.isArchived,
  );
  const archivedMockups = allMockups.filter((mockup) => mockup.isArchived);

  return {
    active: activeMockups,
    missing: missingMockups,
    pending: pendingMockups,
    archived: archivedMockups,
  };
});

const mockupTabs = computed(() => {
  const baseTabs = [
    {
      key: "active",
      title: "Active",
      count: mockupsByTab.value.active.length,
    },
    {
      key: "missing",
      title: "Missing",
      count: mockupsByTab.value.missing.length,
    },
  ];

  if (userHasPermission("Admin.Access")) {
    baseTabs.push({
      key: "pending",
      title: "Pending Approval",
      count: mockupsByTab.value.pending.length,
    });
  }

  baseTabs.push({
    key: "archived",
    title: "Archived",
    count: mockupsByTab.value.archived.length,
  });

  return baseTabs;
});
</script>

<template>
  <div>
    <CardVue>
      <FormSection
        :details-list="detailsList"
        :display-divider="false"
        :uses-column-layout="false"
        title="Kit Mockups"
      >
        <div
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 py-2"
        >
          <div class="flex flex-wrap gap-2 ml-auto mb-2">
            <BaseButton
              icon-name="heroicons:arrow-up-tray"
              label="Bulk Upload"
              variant="primary"
              @click="isPhotoUploadModalOpen = true"
            />
            <BaseButton
              icon-name="heroicons:arrow-down-tray"
              label="Bulk Download"
              size="small"
              variant="secondary"
              @click="onDownloadAllMockups"
            />
            <BaseButton
              class="!border-red-500 !text-red-500"
              data-test="delete-all-mockups-button"
              icon-color="red-500"
              icon-name="heroicons:trash"
              label="Delete All Mockups"
              variant="tertiary"
              @click="onDeleteAllMockups"
            />
            <ConfirmationModal
              v-model:is-open="showDeleteAllMockupsConfirmation"
              data-test="delete-all-mockups-confirmation"
              warning-message="Are you sure you want to delete all mockups for this kit? This action cannot be undone."
              @on-confirm="confirmDeleteAllMockups"
            />
          </div>
        </div>

        <div class="mb-4">
          <HorizontalTabs
            v-model="activeTab"
            :tabs="mockupTabs"
            class="!justify-start"
            value-key="key"
          />
        </div>

        <div class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
          <div
            v-for="mockup in mockupsByTab[activeTab]"
            :key="mockup.id"
            :class="{
              'ring-2 ring-amber-200 border-amber-300':
                !mockup.isApproved && !mockup.isArchived,
            }"
            class="bg-white border border-gray-200 rounded-lg overflow-hidden relative group"
          >
            <div class="flex items-center gap-2 p-2">
              <span class="font-medium text-gray-600">
                {{ mockup.shirtNumber || "#" }}
              </span>

              <div class="flex-1 min-w-0">
                <RouterLinkVue
                  v-if="mockup.athleteId && userHasPermission('Admin.Access')"
                  :to="`/athletes/${mockup.athleteId}/details`"
                  class="text-base font-medium text-indigo-600 hover:text-indigo-800 block truncate"
                >
                  {{ mockup.shirtName || "(Unnamed Athlete)" }}
                </RouterLinkVue>
                <span
                  v-else
                  class="text-base font-medium text-gray-900 block truncate"
                >
                  {{ mockup.shirtName }}
                </span>
              </div>

              <BaseIcon
                v-if="mockup.mockupSetId"
                class="h-4 w-4 text-green-500 ml-1"
                name="heroicons:check-circle"
                aria-label="All frame assets complete"
              />

              <button
                aria-label="Upload frame asset"
                class="flex-shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                type="button"
                @click="openFrameAssetModal(mockup)"
              >
                <BaseIcon
                  class="h-4 w-4"
                  name="heroicons:photo-solid"
                />
              </button>
            </div>

            <div
              v-if="mockup.imageUrls.length > 0"
              class="relative bg-gray-100 flex items-center justify-center cursor-pointer rounded-lg aspect-square"
              @click="
                handleImagePreview(getCurrentImage(mockup)?.imageUrl || '')
              "
            >
              <div
                v-if="imageLoading[mockup.id]"
                class="absolute inset-0 flex items-center justify-center bg-gray-100"
              >
                <LoadingSpinner />
              </div>
              <img
                v-show="!imageLoading[mockup.id]"
                :alt="`Mockup for ${mockup.shirtName || 'player'}`"
                :src="
                  imgixThumbnail(getCurrentImage(mockup)?.imageUrl, {
                    width: 400,
                  })
                "
                class="w-full h-full object-cover p-2"
                @load="imageLoading[mockup.id] = false"
              />

              <button
                v-if="
                  mockup.imageUrls.length > 1 &&
                  getCurrentImageIndex(mockup.id) > 0
                "
                class="absolute left-1 top-1/2 -translate-y-1/2 size-8 bg-white/80 hover:bg-white text-gray-700 flex items-center justify-center rounded-md shadow-sm"
                @click.stop="previousImage(mockup.id)"
              >
                <BaseIcon
                  class="w-4 h-4"
                  name="heroicons:chevron-left"
                />
              </button>

              <button
                v-if="
                  mockup.imageUrls.length > 1 &&
                  getCurrentImageIndex(mockup.id) < mockup.imageUrls.length - 1
                "
                class="absolute right-1 top-1/2 -translate-y-1/2 size-8 bg-white/80 hover:bg-white text-gray-700 flex items-center justify-center rounded-md shadow-sm"
                @click.stop="nextImage(mockup.id)"
              >
                <BaseIcon
                  class="w-4 h-4"
                  name="heroicons:chevron-right"
                />
              </button>

              <div
                v-if="mockup.imageUrls.length > 1"
                class="absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/60 text-white text-xs rounded-md"
              >
                {{ getCurrentImageIndex(mockup.id) + 1 }} /
                {{ mockup.imageUrls.length }}
              </div>

              <button
                class="absolute top-1 left-1 size-8 bg-white/80 hover:bg-white text-indigo-600 flex items-center justify-center rounded-md shadow-sm"
                @click.stop="onDownloadMockup(mockup)"
              >
                <BaseIcon
                  class="w-4 h-4"
                  name="heroicons:arrow-down-tray"
                />
              </button>

              <button
                class="absolute top-1 right-1 size-8 bg-white/80 hover:bg-white text-red-500 flex items-center justify-center rounded-md shadow-sm"
                @click.stop="
                  onRemoveImage(
                    mockup.id,
                    getCurrentImage(mockup)?.imageUrl || '',
                  )
                "
              >
                <BaseIcon
                  class="w-4 h-4"
                  name="heroicons:x-mark"
                />
              </button>

              <button
                class="absolute bottom-1 right-1 size-8 bg-white/80 hover:bg-white text-green-600 flex items-center justify-center rounded-md shadow-sm"
                @click.stop="triggerUpload(mockup.id)"
              >
                <BaseIcon
                  class="w-4 h-4"
                  name="heroicons:plus"
                />
              </button>

              <button
                v-if="
                  activeTab === 'pending' && userHasPermission('Admin.Access')
                "
                class="absolute bottom-10 right-1 size-8 bg-white/80 hover:bg-white text-blue-600 flex items-center justify-center rounded-md shadow-sm"
                @click.stop="
                  router.push(
                    `/teams/${kit.teamId}/rosters/${kit.rosterId}/details`,
                  )
                "
              >
                <BaseIcon
                  class="w-4 h-4"
                  name="heroicons:arrow-top-right-on-square"
                />
              </button>
            </div>
            <div
              v-else
              class="aspect-square bg-gray-100 flex items-center justify-center relative"
            >
              <button
                v-if="activeTab === 'pending'"
                class="flex flex-col items-center justify-center h-full text-center p-4 w-full rounded-lg group bg-amber-50 hover:bg-amber-100"
                @click="
                  router.push(
                    `/teams/${kit.teamId}/rosters/${kit.rosterId}/details`,
                  )
                "
              >
                <div
                  class="flex items-center justify-center w-12 h-12 bg-amber-200 rounded-full mb-3 group-hover:bg-amber-300"
                >
                  <BaseIcon
                    class="w-6 h-6 text-amber-700"
                    name="heroicons:exclamation-triangle"
                  />
                </div>
                <span class="text-sm font-semibold text-amber-800 mb-1">
                  Approval Required
                </span>
                <div
                  class="flex items-center gap-1 text-xs text-amber-700 font-medium"
                >
                  <span>Go to Roster</span>
                  <BaseIcon
                    class="w-3 h-3"
                    name="heroicons:arrow-top-right-on-square"
                  />
                </div>
              </button>
              <FormKit
                v-else
                :clear="clearUploaderInput"
                :ignore="true"
                :multiple="true"
                :show-preview="false"
                class="!aspect-square !w-full !h-full !-mb-3.5 !mt-0"
                type="uploader"
                @input="handleImageUpload(mockup, $event as string | string[])"
              />
            </div>

            <div class="-mb-3.5">
              <FormKit
                :id="`uploader-${mockup.id}`"
                :multiple="true"
                class="hidden"
                type="uploader"
                @input="handleImageUpload(mockup, $event as string | string[])"
              />
            </div>
          </div>
          <div
            v-if="mockupsByTab[activeTab].length === 0"
            class="col-span-full text-center text-gray-500"
          >
            No mockups found for this tab
          </div>
        </div>
      </FormSection>
      <CatalogKitBulkUploader
        v-if="kit.rosterId"
        :key="String(isPhotoUploadModalOpen)"
        v-model="isPhotoUploadModalOpen"
        :kit="kit"
      />
      <ModalVue
        v-model:is-open="isImagePreviewModalOpen"
        size="medium"
      >
        <img
          :src="previewImageUrl"
          class="w-full"
        />
      </ModalVue>
    </CardVue>

    <FrameAssetModal
      v-if="selectedMockup"
      v-model:is-open="isFrameAssetModalOpen"
      :kit-id="kit.id"
      :mockup="selectedMockup"
      @on-success="closeFrameAssetModal"
    />
  </div>
</template>

<style lang="postcss">
/* Remove webkit number input arrows */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
